import type {
  CreativesConfigComponentKeys,
  CreativesConfigComponentMetaConfig,
  DefaultCreativesConfig,
} from '@ad-lucy/config/creatives';
import type { ColorFormat, ImageMeta } from '@ad-lucy/utils';
import type { StickyAlignment, StickyPosition } from '@ad-lucy/utils/scaling/types';
import type { StateUpdater } from 'preact/hooks';
import type { Dispatch, ReactElement } from 'react';

export enum ConfigName {
  GLOBAL = 'global',
  STORES = 'stores',
  CONTENT = 'content',
  GHA = 'googleHeavyAds',
}

export interface AdDataProviderProps {
  children: ReactElement | ReactElement[];
  contentUrl?: string;
  globalUrl?: string;
  locationsUrl?: string;
  locationsLightUrl?: string;
  storesUrl?: string;
}

export interface AdContextValue {
  operations: {
    setContentUrl: (id: string) => void;
    setFullContentUrl: (url: string) => void;
    setData: Record<ConfigName, Dispatch<StateUpdater<boolean>>>;
  };
}

export interface AdData<TData> {
  loading: boolean;
  data: TData | undefined;
  error: string | undefined;
}

export interface AdDataContextValue<TData> {
  data: AdData<TData>;
}

export interface RawMeta {
  [key: string]: unknown;
}

export type ParentComponentConfig<
  Name extends CreativesConfigComponentKeys = CreativesConfigComponentKeys,
> = Omit<ComponentConfig<Name>, 'children'> | null | undefined;

/**
 * A mapped type that translates a component's meta-configuration from the creative's config
 * into corresponding TypeScript types. It inspects the `type` property of the meta-config
 * and returns the associated primitive or complex type.
 *
 * This is a core utility for ensuring type safety between the creative configuration
 * and the component's props.
 *
 * @template T - The meta-configuration object for a specific component property.
 */
type MetaTypeLiteral =
  | 'string'
  | 'text'
  | 'html'
  | 'url'
  | 'email'
  | 'image_map_coords'
  | 'productId'
  | 'datetime'
  | 'country'
  | 'brand'
  | 'category'
  | 'integer'
  | 'float'
  | 'range'
  | 'referenceId'
  | 'boolean'
  | 'color_picker'
  | 'sticky_position'
  | 'sticky_align'
  | 'array'
  | 'image'
  | 'image_map'
  | 'object'
  | 'json'
  | 'enum';

type PrimitiveMetaTypes =
  | 'string'
  | 'text'
  | 'html'
  | 'url'
  | 'email'
  | 'image_map_coords'
  | 'productId'
  | 'datetime'
  | 'country'
  | 'brand'
  | 'category';

type NumericMetaTypes = 'integer' | 'float' | 'range' | 'referenceId';
type ResolveEnum<T> = T extends { oneOf: readonly (infer U)[] } ? U & string : never;

type ResolveObject<T> = T extends { structure: infer S }
  ? { [K in keyof S]: MetaTypesMap<Extract<S[K], CreativesConfigComponentMetaConfig>> }
  : never;

type ResolveArray<T> = T extends { items: infer I }
  ? MetaTypesMap<Extract<I, CreativesConfigComponentMetaConfig>>[]
  : never;

type ResolvePrimitive<Type> = Type extends PrimitiveMetaTypes ? string : never;

type ResolveNumeric<Type> = Type extends NumericMetaTypes ? number : never;

type ResolveMetaType<
  T extends CreativesConfigComponentMetaConfig,
  Type extends MetaTypeLiteral,
> = Type extends 'enum'
  ? ResolveEnum<T>
  : Type extends PrimitiveMetaTypes
    ? ResolvePrimitive<Type>
    : Type extends NumericMetaTypes
      ? ResolveNumeric<Type>
      : Type extends 'boolean'
        ? boolean
        : Type extends 'color_picker'
          ? ColorFormat
          : Type extends 'sticky_position'
            ? StickyPosition
            : Type extends 'sticky_align'
              ? StickyAlignment
              : Type extends 'array'
                ? ResolveArray<T>
                : Type extends 'image' | 'image_map'
                  ? ImageMeta | string
                  : Type extends 'object' | 'json'
                    ? ResolveObject<T>
                    : never;

type MetaTypesMap<T extends CreativesConfigComponentMetaConfig> =
  T['type'] extends readonly (infer U)[] // Multi-type definition (e.g. ['json','array'])
    ? ResolveMetaType<T, Extract<U, MetaTypeLiteral>>
    : ResolveMetaType<T, Extract<T['type'], MetaTypeLiteral>>;

/**
 * Creates a typed `meta` object for a specific component based on its configuration
 * in `DefaultCreativesConfig`.
 *
 * It iterates over the `meta` properties defined for a component in the configuration
 * and uses `MetaTypesMap` to resolve each property to its correct TypeScript type.
 *
 * @template T - The name of the component, which must be a key in `DefaultCreativesConfig['components']`.
 */
export type ComponentMeta<T extends keyof DefaultCreativesConfig['components']> = {
  [K in keyof DefaultCreativesConfig['components'][T]['meta']]: MetaTypesMap<
    Extract<DefaultCreativesConfig['components'][T]['meta'][K], CreativesConfigComponentMetaConfig>
  >;
};

export interface ComponentConfig<
  Name extends CreativesConfigComponentKeys = CreativesConfigComponentKeys,
  ParentName extends CreativesConfigComponentKeys = CreativesConfigComponentKeys,
> {
  readonly name: string | null;
  readonly className?: string;
  readonly id: string;
  readonly meta: ComponentMeta<Name>;
  readonly parent?: ParentComponentConfig<ParentName>;
  readonly page?: string;
  readonly column: number;
  readonly component: {
    readonly id: string;
    readonly name: Name;
  };
  readonly product?: string;
  readonly children?: ReactElement[] | ReactElement[][];
  readonly sorting?: number;
}
